import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { MessageCircle, Plus } from "lucide-react"
import { Card, Toast, ToastType, Button } from "../../../../components/ui"
import { apiService } from "../../../../services/api"
import { WhatsAppConversationList } from "../../../whatsapp/components/WhatsAppConversationList"
import { WhatsAppChatWidget } from "../../../whatsapp/components/WhatsAppChatWidget"
import { NewConversationDialog } from "../../../conversations/components/NewConversationDialog"
import { ConversationMetrics } from "../../../conversations/components/ConversationMetrics"
import { ConversationRightPanel } from "../../../conversations/components/ConversationRightPanel"

function formatAverageResponseLabel(
  minutes: number | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (minutes === null || Number.isNaN(minutes)) {
    return t("conversations.metrics.noData")
  }
  if (minutes < 1) {
    return t("conversations.metrics.lessThanMinute")
  }
  if (minutes < 60) {
    return t("conversations.metrics.minutes", { count: Math.round(minutes) })
  }
  const hours = Math.floor(minutes / 60)
  const remainder = Math.round(minutes % 60)
  if (remainder <= 0) {
    return t("conversations.metrics.hours", { count: hours })
  }
  return t("conversations.metrics.hoursMinutes", { hours, minutes: remainder })
}

export const ConversationsPage: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const lastConversationDeepLinkRef = useRef<string | null>(null)
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null)
  const [selectedLeadId, setSelectedLeadId] = useState<string | undefined>(undefined)
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
  const [showNewConversation, setShowNewConversation] = useState(false)
  const [metricsPayload, setMetricsPayload] = useState<{
    averageResponseTimeMinutes: number | null
    responseRatePercent: number
    conversionRatePercent: number
  } | null>(null)
  const [inboxListRefresh, setInboxListRefresh] = useState(0)
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<string | null>(null)
  const [configs, setConfigs] = useState<Array<{
    id: string
    phoneNumberId: string
    businessAccountId: string
    displayPhone: string
    accountName: string | null
    isPrimary: boolean
    isActive: boolean
    createdAt: string
  }>>([])
  const [loadingConfigs, setLoadingConfigs] = useState(true)

  const handleSelectConversation = useCallback((phoneNumber: string, leadId?: string) => {
    setSelectedPhone(phoneNumber)
    setSelectedLeadId(leadId)
    void (async () => {
      try {
        if (selectedPhoneNumberId) {
          await apiService.markWhatsAppConversationRead(phoneNumber, selectedPhoneNumberId)
          setInboxListRefresh((n) => n + 1)
        }
      } catch {}
    })()
  }, [selectedPhoneNumberId])

  const leadIdParam = searchParams.get("leadId")
  const phoneParam = searchParams.get("phone")

  useEffect(() => {
    if (!leadIdParam && !phoneParam) {
      lastConversationDeepLinkRef.current = null
      return
    }

    // Wait for config to load before processing deep link
    if (!selectedPhoneNumberId) {
      return
    }

    const fingerprint = `${leadIdParam ?? ""}|${phoneParam ?? ""}`
    if (lastConversationDeepLinkRef.current === fingerprint) return
    lastConversationDeepLinkRef.current = fingerprint

    const clearQuery = (): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete("leadId")
          next.delete("phone")
          return next
        },
        { replace: true },
      )
    }

    let cancelled = false
    void (async () => {
      try {
        if (phoneParam) {
          handleSelectConversation(phoneParam, leadIdParam ?? undefined)
          clearQuery()
          return
        }
        if (leadIdParam) {
          const result = await apiService.getWhatsAppConversations({
            leadId: leadIdParam,
            page: 1,
            limit: 50,
          })
          const first = result?.conversations?.[0]
          if (!cancelled && first) {
            handleSelectConversation(first.phoneNumber, leadIdParam)
          }
          clearQuery()
        }
      } catch {
        clearQuery()
      }
    })()

    return () => {
      cancelled = true
    }
  }, [leadIdParam, phoneParam, setSearchParams, handleSelectConversation, selectedPhoneNumberId])

  const handleConversationCreated = useCallback((phoneNumber: string, leadId: string) => {
    setSelectedPhone(phoneNumber)
    setSelectedLeadId(leadId)
    setShowNewConversation(false)
    setToast({ message: t("conversations.success.newConversationCreated"), type: "success" })
    void (async () => {
      try {
        if (selectedPhoneNumberId) {
          await apiService.markWhatsAppConversationRead(phoneNumber, selectedPhoneNumberId)
          setInboxListRefresh((n) => n + 1)
        }
      } catch {}
    })()
  }, [t, selectedPhoneNumberId])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const configsData = await apiService.getWhatsAppConfigs()
        if (!cancelled) {
          setConfigs(configsData)
          if (configsData.length > 0) {
            const primary = configsData.find(c => c.isPrimary) || configsData[0]
            setSelectedPhoneNumberId(primary.phoneNumberId)
          }
        }
      } catch {
        if (!cancelled) {
          setConfigs([])
        }
      } finally {
        setLoadingConfigs(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!selectedPhoneNumberId) return
    let cancelled = false
    void (async () => {
      try {
        const data = await apiService.getWhatsAppConversationMetrics(selectedPhoneNumberId)
        if (!cancelled) {
          setMetricsPayload(data)
        }
      } catch {
        if (!cancelled) {
          setMetricsPayload(null)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [selectedPhoneNumberId])

  const metrics = useMemo(
    () => ({
      averageResponseTime: formatAverageResponseLabel(
        metricsPayload?.averageResponseTimeMinutes ?? null,
        t,
      ),
      responseRate: metricsPayload?.responseRatePercent ?? 0,
      conversionRate: metricsPayload?.conversionRatePercent ?? 0,
    }),
    [metricsPayload, t],
  )

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <MessageCircle className="h-6 w-6" />
            {t("whatsapp.inbox.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("whatsapp.inbox.subtitle")}
          </p>
        </div>
        <Button onClick={() => setShowNewConversation(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          {t("conversations.actions.newConversation")}
        </Button>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="flex h-[calc(100vh-220px)] min-h-[500px]">
          <div className="w-80 border-r border-border bg-card flex-shrink-0 flex flex-col">
            {configs.length > 1 && !loadingConfigs && (
              <div className="p-3 border-b border-border">
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Conta WhatsApp
                </label>
                <select
                  value={selectedPhoneNumberId || ''}
                  onChange={(e) => {
                    setSelectedPhoneNumberId(e.target.value)
                    setSelectedPhone(null)
                  }}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {configs.map((config) => (
                    <option key={config.phoneNumberId} value={config.phoneNumberId}>
                      {config.accountName || config.displayPhone}
                      {config.isPrimary ? ' (Principal)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex-1 overflow-hidden">
              <WhatsAppConversationList
                onSelectConversation={handleSelectConversation}
                selectedPhoneNumber={selectedPhone}
                listRefreshTrigger={inboxListRefresh}
                phoneNumberId={selectedPhoneNumberId || undefined}
              />
            </div>
            <ConversationMetrics metrics={metrics} />
          </div>

          <div className="flex-1 flex min-w-0 items-stretch bg-surface">
            {selectedPhone ? (
              <>
                <div className="flex-1 min-w-0 flex flex-col p-4">
                  <WhatsAppChatWidget
                    phoneNumber={selectedPhone}
                    leadId={selectedLeadId}
                    className="border-0 shadow-none h-full min-h-0"
                  />
                </div>
                <ConversationRightPanel
                  phoneNumber={selectedPhone}
                  leadId={selectedLeadId}
                  onLeadOrVehicleUpdated={() => setInboxListRefresh((n) => n + 1)}
                />
              </>
            ) : (
              <div className="flex flex-col items-center text-center p-8">
                <MessageCircle className="h-16 w-16 text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-foreground">
                  {t("whatsapp.inbox.selectConversation")}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("whatsapp.inbox.selectConversationHint")}
                </p>
              </div>
            )}
          </div>
        </div>
      </Card>

      <NewConversationDialog
        open={showNewConversation}
        onOpenChange={setShowNewConversation}
        onConversationCreated={handleConversationCreated}
      />
    </div>
  )
}
